import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, ChevronLeft, ChevronRight, Minus, PenLine, Ruler, UserRound, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { InfiniteSlider } from '../components/ui/infinite-slider';
import OptimizedImage from '../components/common/OptimizedImage';
import PlayerShopSection from '../components/player/PlayerShopSection';
import JerseyCustomizationPreview from '../components/JerseyCustomizationPreview';
import { players } from '../data/players';
import { getProductAvailability } from '../data/catalog';

const featuredProducts = [
  {
    title: 'Home Kit 2026/27',
    price: '129 DT',
    badge: 'New Season',
    image: '/assets/home 1.png',
  },
  {
    title: 'Away Kit 2026/27',
    price: '129 DT',
    badge: 'New Season',
    image: '/assets/créer maillot/away kit.jpg',
  },
  {
    title: 'Goalkeeper Kit 2026/27',
    price: '129 DT',
    badge: 'New Season',
    image: '/assets/créer maillot/Goalkeeper Kit.png',
  },
];

const customizerProduct = {
  id: 'cab-home-kit-2627',
  name: 'CAB 26/27 HOME KIT',
  shortName: 'HOME KIT',
  price: 129,
  image: '/assets/créer maillot/home kit devant .png',
  accent: '#f5c400',
  description: 'Le maillot domicile officiel, pensé pour les soirs de match et la fierté de Bizerte.',
};

const partnerSliderItems = [
  { title: 'Van Laack', image: '/assets/slider/vanlaack.png' },
  { title: 'Uhlsport', image: '/assets/slider/uhlsport.png' },
  { title: 'Site officiel CAB', image: '/assets/slider/site%20officiel.png' },
  { title: 'RS', image: '/assets/slider/RS.png' },
  { title: 'PAEB', image: '/assets/slider/PAEB.png' },
  { title: 'Officia', image: '/assets/slider/officia.png' },
  { title: 'Express', image: '/assets/slider/express.png' },
  { title: 'Agil', image: '/assets/slider/agil.png' },
];

const galleryLabels = ['FRONT', 'PLAYER', 'BACK', 'MANNEQUIN'];
const sizeGuideRows = [
  { size: 'S', chest: '88–96' },
  { size: 'M', chest: '96–104' },
  { size: 'L', chest: '104–112' },
  { size: 'XL', chest: '112–120' },
  { size: 'XXL', chest: '120–128' },
  { size: '3XL', chest: '128–136' },
  { size: '4XL', chest: '136–144' },
];
const getPlayerShirtName = (name) => name.trim().split(/\s+/).pop() || '';

const Home = () => {
  const { addToCartAnimated } = useCart();
  const heroRef = useRef(null);
  const [productStock, setProductStock] = useState(null);
  const [selectedProduct] = useState(customizerProduct);
  const [activeView, setActiveView] = useState(0);
  const [selectedSize, setSelectedSize] = useState('M');
  const [personalizationType, setPersonalizationType] = useState('none');
  const [selectedPlayer, setSelectedPlayer] = useState(players[0]);
  const [playerName, setPlayerName] = useState('');
  const [playerNumber, setPlayerNumber] = useState('10');
  const [isAdded, setIsAdded] = useState(false);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [magnifierPosition, setMagnifierPosition] = useState(null);

  useEffect(() => {
    let mounted = true;
    getProductAvailability(selectedProduct.id).then((inStock) => {
      if (mounted) setProductStock(new Map([[selectedProduct.id, inStock]]));
    }).catch((error) => {
      console.error('Error loading product availability:', error);
    });
    return () => { mounted = false; };
  }, [selectedProduct.id]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || typeof window.matchMedia !== 'function') return undefined;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(min-width: 901px) and (hover: hover) and (pointer: fine)');

    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let frameId = null;

    const animate = () => {
      currentX += (targetX - currentX) * 0.045;
      currentY += (targetY - currentY) * 0.045;

      const settled = Math.abs(targetX - currentX) < 0.005 && Math.abs(targetY - currentY) < 0.005;
      if (settled) {
        currentX = targetX;
        currentY = targetY;
      }

      hero.style.setProperty('--hero-bg-x', `${currentX * 1.5}px`);
      hero.style.setProperty('--hero-bg-y', `${currentY * 1.5}px`);
      hero.style.setProperty('--hero-detail-x', `${currentX * 3.5}px`);
      hero.style.setProperty('--hero-detail-y', `${currentY * 3.5}px`);

      frameId = settled ? null : window.requestAnimationFrame(animate);
    };

    const scheduleAnimation = () => {
      if (frameId === null) frameId = window.requestAnimationFrame(animate);
    };

    const handlePointerMove = (event) => {
      if (event.pointerType === 'touch' || reducedMotion.matches || !finePointer.matches) return;
      const bounds = hero.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;

      const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
      const y = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
      targetX = -Math.max(-1, Math.min(1, x));
      targetY = -Math.max(-1, Math.min(1, y));
      scheduleAnimation();
    };

    const handlePointerLeave = () => {
      targetX = 0;
      targetY = 0;
      scheduleAnimation();
    };

    hero.addEventListener('pointermove', handlePointerMove);
    hero.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      hero.removeEventListener('pointermove', handlePointerMove);
      hero.removeEventListener('pointerleave', handlePointerLeave);
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, []);

  const galleryImages = selectedProduct.id === 'cab-home-kit-2627'
    ? [
      '/assets/créer maillot/home kit devant .png',
      '/assets/joueurs/dridi.png',
      '/assets/créer maillot/home kit arriére.png',
      '/assets/créer maillot/model.png',
    ]
    : selectedProduct.id === 'cab-away-kit-2627'
      ? [
        '/assets/créer maillot/away kit devant.png',
        '/assets/créer maillot/joueur away kit.png',
        '/assets/créer maillot/away kit arriére.png',
        '/assets/créer maillot/mannequin away kit.png',
      ]
      : [
        '/assets/créer maillot/goalkeeper avant.png',
        '/assets/joueurs/hanzouli.png',
        '/assets/créer maillot/goalkeeper dérriere.png',
        '/assets/créer maillot/mannequin goalkeeper.png',
      ];
  const personalizationPrice = personalizationType === 'player' ? 15 : personalizationType === 'custom' ? 20 : 0;
  const currentPrice = selectedProduct.price + personalizationPrice;
  const outOfStock = productStock !== null && productStock.get(selectedProduct.id) !== true;
  const isMagnifierEnabled = activeView === 0 || activeView === 2;

  const selectPersonalization = (nextType) => {
    setPersonalizationType(nextType);
    setActiveView(nextType === 'none' ? 0 : 2);
    if (nextType === 'player') {
      setPlayerName(getPlayerShirtName(selectedPlayer.name));
      setPlayerNumber(selectedPlayer.number === '—' ? '' : selectedPlayer.number);
    }
  };

  const moveGallery = (direction) => {
    setActiveView((current) => (current + direction + galleryImages.length) % galleryImages.length);
  };

  const handleAddToCart = (event) => {
    if (outOfStock) return;
    const product = {
      id: `${selectedProduct.id}-${personalizationType}-${playerName.trim().toUpperCase() || 'blank'}-${playerNumber || 'blank'}-${selectedSize}`,
      productId: selectedProduct.id,
      name: `${selectedProduct.name}${personalizationType !== 'none' ? ' / CUSTOM' : ''}`,
      price: currentPrice,
      image: selectedProduct.image,
      size: selectedSize,
      customization: personalizationType !== 'none' ? { name: playerName.trim().toUpperCase(), number: playerNumber } : null,
    };
    const sourceImage = event.currentTarget.closest('.cab-customizer__layout')?.querySelector('.cab-customizer__stage img');
    addToCartAnimated(product, sourceImage);
    setIsAdded(true);
    window.setTimeout(() => setIsAdded(false), 1800);
  };

  const handleMagnifierMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;
    setMagnifierPosition({ x, y });
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#050505]">
      <section className="cab-hero" ref={heroRef}>
        <OptimizedImage className="cab-hero__backdrop" src="/assets/home 2.jpg" alt="Black CAB jersey with gold Arabic embroidery" loading="eager" fetchPriority="high" sizes="100vw" />
        <OptimizedImage className="cab-hero__detail" src="/assets/home 2.jpg" alt="" aria-hidden="true" loading="eager" sizes="100vw" />
        <div className="cab-hero__wash" />
        <div className="cab-hero__light" />
        <div className="cab-hero__grid" />
        <div className="cab-hero__grain" />

        <div className="cab-hero__campaign"><span>01</span><i /><span>03</span></div>
        <div className="cab-hero__scroll"><span>Scroll to discover</span><i /></div>
      </section>

      <section className="border-y border-white/10 bg-[#070707] py-7 md:py-9" aria-label="CAB partners">
        <div className="cab-container mb-5 flex items-center justify-between gap-4">
          <span className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-[0.2em] text-white/55"><i className="h-px w-6 bg-[#f5c400]" /> NOS PARTENAIRES</span>
          <span className="hidden text-[9px] uppercase tracking-[0.18em] text-white/35 sm:block">CLUB ATHLÉTIQUE BIZERTIN</span>
        </div>
        <InfiniteSlider gap={24} duration={32} durationOnHover={48} reverse className="w-full">
          {partnerSliderItems.map((item) => (
            <div key={item.title} className="flex h-[76px] w-[190px] shrink-0 items-center justify-center border-r border-white/10 px-5 md:w-[220px]">
              <OptimizedImage src={item.image} alt={item.title} loading="lazy" sizes="220px" className="h-full w-full object-contain" />
            </div>
          ))}
        </InfiniteSlider>
      </section>

      <section className="cab-section cab-season" aria-labelledby="season-title">
        <div className="cab-season__watermark" aria-hidden="true">CAB<br /><span>1928</span></div>
        <div className="cab-container">
          <div className="cab-season__header">
            <div className="cab-season__title-wrap">
              <div className="cab-season__eyebrow"><span /> CAB STORE / 2026—27 COLLECTION</div>
              <h2 id="season-title"><span>New Season</span><strong>Essentials</strong></h2>
            </div>
            <Link to="/shop" className="cab-season__collection-link">
              <span>View collection</span><ArrowRight size={17} />
            </Link>
          </div>
          <div className="cab-season__rule" />

          <div className="cab-season__meta" aria-hidden="true"><span>THE NEW ERA</span><i /><span>01 / 03</span></div>
          <div className="cab-season__grid">
            {featuredProducts.map((product, index) => (
              <motion.article
                key={product.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className={`cab-season__card cab-season__card--${index + 1}`}
              >
                <OptimizedImage src={product.image} alt={product.title} sizes="(min-width: 901px) 33vw, 100vw" />
                <div className="cab-season__card-shade" />
                <div className="cab-season__badge">{product.badge}</div>
                <div className="cab-season__card-index">0{index + 1} <span>/ 03</span></div>
                <div className="cab-season__card-content">
                  <div className="cab-season__collection">CAB Collection</div>
                  <h3>{product.title}</h3>
                  <div className="cab-season__card-footer">
                    <span className="cab-season__price">{product.price}</span>
                    <Link to="/shop" className="cab-season__cart-link">Add to cart <ArrowRight size={15} /></Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section id="customizer-title" className="cab-customizer" aria-labelledby="customizer-heading">
        <div className="cab-container">
          <div className="cab-customizer__intro">
            <div>
              <div className="cab-customizer__eyebrow"><span /> CAB STORE / OFFICIAL KIT BUILDER</div>
              <h2 id="customizer-heading">FAIS-EN TON <strong>MAILLOT</strong></h2>
            </div>
            <div className="cab-customizer__edition"><span>02</span><i /><small>2026 / 27<br />BIZERTE</small></div>
          </div>

          <div className="cab-customizer__layout">
            <aside className="cab-customizer__gallery" aria-label="Galerie produit">
              <div className="cab-customizer__thumbs">
                {galleryImages.map((image, index) => (
                  <button type="button" key={`${image}-${index}`} className={`cab-customizer__thumb ${activeView === index ? 'is-active' : ''}`} onClick={() => setActiveView(index)} aria-label={galleryLabels[index]} aria-pressed={activeView === index}>
                    <OptimizedImage src={image} alt="" sizes="82px" />
                    <span>{String(index + 1).padStart(2, '0')}<em>{galleryLabels[index]}</em></span>
                  </button>
                ))}
              </div>
            </aside>

            <div className="cab-customizer__preview-column">
              <div className="cab-customizer__stage-wrap">
                <button type="button" className="cab-customizer__arrow cab-customizer__arrow--previous" onClick={() => moveGallery(-1)} aria-label="Vue précédente"><ChevronLeft size={20} /></button>
                <motion.div className={`cab-customizer__stage cab-customizer__stage--view-${activeView}`} key={`${selectedProduct.id}-${activeView}-${personalizationType}`} initial={{ opacity: 0, scale: .98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: .4 }} onMouseMove={isMagnifierEnabled ? handleMagnifierMove : undefined} onMouseLeave={() => setMagnifierPosition(null)} style={isMagnifierEnabled && magnifierPosition ? { '--magnifier-x': `${magnifierPosition.x}%`, '--magnifier-y': `${magnifierPosition.y}%` } : undefined}>
                  {personalizationType !== 'none' && activeView === 2 ? (
                    <JerseyCustomizationPreview image={galleryImages[activeView]} name={playerName} number={playerNumber} alt={`${selectedProduct.name} ${galleryLabels[activeView]}`} />
                  ) : (
                    <OptimizedImage src={galleryImages[activeView]} alt={`${selectedProduct.name} ${galleryLabels[activeView]}`} sizes="(min-width: 901px) 55vw, 100vw" />
                  )}
                  {isMagnifierEnabled && magnifierPosition && (
                    <div className="cab-customizer__magnifier" aria-hidden="true">
                      {personalizationType !== 'none' && activeView === 2 ? (
                        <JerseyCustomizationPreview image={galleryImages[activeView]} name={playerName} number={playerNumber} alt="" />
                      ) : (
                        <OptimizedImage src={galleryImages[activeView]} alt="" sizes="(min-width: 901px) 55vw, 100vw" />
                      )}
                    </div>
                  )}
                  <div className="cab-customizer__stage-shade" />
                </motion.div>
                <button type="button" className="cab-customizer__arrow cab-customizer__arrow--next" onClick={() => moveGallery(1)} aria-label="Vue suivante"><ChevronRight size={20} /></button>
              </div>
            </div>

            <div className="cab-customizer__panel">
              <div className="cab-customizer__product-kicker">CAB COLLECTION / OFFICIAL</div>
              <h3>{selectedProduct.name}</h3>
              <p className="cab-customizer__description">{selectedProduct.description}</p>
              <div className="cab-customizer__headline-price">{selectedProduct.price} <small>TND</small></div>

              <div className="cab-customizer__block cab-customizer__size-block">
                <div className="cab-customizer__block-heading"><span>TAILLE</span><button type="button" className="cab-size-guide__trigger" onClick={() => setIsSizeGuideOpen(true)}>Guide des tailles <ArrowRight size={12} /></button></div>
                <div className="cab-customizer__sizes">{['S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'].map((size) => <button type="button" key={size} className={selectedSize === size ? 'is-selected' : ''} onClick={() => setSelectedSize(size)} aria-pressed={selectedSize === size}>{size}</button>)}</div>
              </div>

              <div id="customizer-personalization" className="cab-customizer__block">
                <div className="cab-customizer__block-heading"><span>PERSONNALISATION</span><small>01</small></div>
                <div className="cab-customizer__personalization-options">
                  <button type="button" className={personalizationType === 'none' ? 'is-selected' : ''} onClick={() => selectPersonalization('none')} aria-pressed={personalizationType === 'none'}><Minus size={18} /><b>AUCUNE</b><small>0 TND</small></button>
                  <button type="button" className={personalizationType === 'player' ? 'is-selected' : ''} onClick={() => selectPersonalization('player')} aria-pressed={personalizationType === 'player'}><UserRound size={18} /><b>JOUEUR</b><small>+15 TND</small></button>
                  <button type="button" className={personalizationType === 'custom' ? 'is-selected' : ''} onClick={() => selectPersonalization('custom')} aria-pressed={personalizationType === 'custom'}><PenLine size={18} /><b>PERSONNALISÉ</b><small>+20 TND</small></button>
                </div>
              </div>

              {personalizationType === 'custom' && (
                <motion.div className="cab-customizer__personalization" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ duration: .35 }}>
                  <div className="cab-customizer__fields">
                    <label><span>NOM DU MAILLOT</span><input value={playerName} onChange={(event) => setPlayerName(event.target.value.replace(/[^a-zA-ZÀ-ÿ ]/g, '').slice(0, 14))} placeholder="EX. MADDEH" maxLength="14" /></label>
                    <label><span>NUMÉRO</span><input value={playerNumber} onChange={(event) => setPlayerNumber(event.target.value.replace(/\D/g, '').slice(0, 2))} placeholder="10" inputMode="numeric" maxLength="2" /></label>
                  </div>
                </motion.div>
              )}

              {personalizationType === 'player' && (
                <motion.div className="cab-customizer__player-list" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                  <span className="cab-customizer__sub-label">CHOISIR UN JOUEUR / +15 TND</span>
                  {players.filter((player) => player.number && player.number !== '—').map((player) => (
                    <button type="button" key={player.id} className={selectedPlayer.id === player.id ? 'is-selected' : ''} onClick={() => { setSelectedPlayer(player); setPlayerName(getPlayerShirtName(player.name)); setPlayerNumber(player.number); setActiveView(2); }} aria-pressed={selectedPlayer.id === player.id}>
                      <span className="cab-customizer__player-avatar">{player.image ? <OptimizedImage src={player.image} alt="" loading="lazy" sizes="56px" /> : <b>#{player.number}</b>}</span>
                      <span className="cab-customizer__player-copy"><b>{player.name}</b><small>{player.position}</small></span>
                      <strong>#{player.number}</strong>
                    </button>
                  ))}
                </motion.div>
              )}

              <div className="cab-customizer__buy">
                <div><span>TOTAL</span><strong>{currentPrice} <small>TND</small></strong></div>
                <button type="button" className={`cab-customizer__add ${isAdded ? 'is-added' : ''}`} onClick={handleAddToCart} disabled={outOfStock}>{outOfStock ? 'RUPTURE DE STOCK' : isAdded ? <><Check size={17} /> AJOUTÉ</> : <>AJOUTER AU PANIER <ArrowRight size={17} /></>}</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={isSizeGuideOpen} onClose={setIsSizeGuideOpen} className="cab-size-guide">
        <div className="cab-size-guide__backdrop" aria-hidden="true" />
        <DialogPanel className="cab-size-guide__panel">
          <header className="cab-size-guide__header">
            <div className="cab-size-guide__brandline"><span><i /> CAB STORE / FIT GUIDE</span><small>COLLECTION 2026—27</small></div>
            <button type="button" className="cab-size-guide__close" onClick={() => setIsSizeGuideOpen(false)} aria-label="Fermer le guide des tailles"><X size={19} /></button>
            <p className="cab-size-guide__eyebrow">LE BON AJUSTEMENT, SANS HÉSITER</p>
            <DialogTitle as="h2">TROUVEZ <em>VOTRE TAILLE</em></DialogTitle>
            <p className="cab-size-guide__intro">Prenez vos mesures et choisissez la coupe dans laquelle vous jouerez vos plus beaux matchs.</p>
          </header>

          <div className="cab-size-guide__content">
            <section className="cab-size-guide__measure" aria-labelledby="cab-size-measure-title">
              <div className="cab-size-guide__measure-icon"><Ruler size={20} strokeWidth={1.6} /></div>
              <div>
                <span className="cab-size-guide__section-index">01 / MESURE</span>
                <h3 id="cab-size-measure-title">Le tour de poitrine</h3>
                <p>Placez un mètre ruban sous les bras, autour de la partie la plus large de votre poitrine. Gardez-le horizontal, sans serrer.</p>
              </div>
            </section>

            <section className="cab-size-guide__chart" aria-labelledby="cab-size-chart-title">
              <div className="cab-size-guide__chart-heading">
                <div><span className="cab-size-guide__section-index">02 / CORRESPONDANCE</span><h3 id="cab-size-chart-title">Repères de taille <small>CM</small></h3></div>
                <span className="cab-size-guide__selected">VOTRE CHOIX <b>{selectedSize}</b></span>
              </div>
              <div className="cab-size-guide__table-wrap">
                <table>
                  <thead><tr><th scope="col">TAILLE</th><th scope="col">TOUR DE POITRINE</th><th scope="col"><span className="cab-size-guide__sr-only">Sélection</span></th></tr></thead>
                  <tbody>
                    {sizeGuideRows.map(({ size, chest }) => (
                      <tr key={size} className={selectedSize === size ? 'is-selected' : ''}>
                        <th scope="row"><button type="button" onClick={() => { setSelectedSize(size); setIsSizeGuideOpen(false); }} aria-label={`Choisir la taille ${size}`} aria-pressed={selectedSize === size}>{size}</button></th>
                        <td>{chest}</td>
                        <td>{selectedSize === size ? <span className="cab-size-guide__current">SÉLECTIONNÉE</span> : <button type="button" className="cab-size-guide__choose" onClick={() => { setSelectedSize(size); setIsSizeGuideOpen(false); }}>CHOISIR <ArrowRight size={12} /></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="cab-size-guide__note">Repères indicatifs. Si vous êtes entre deux tailles, choisissez la plus grande pour plus d’aisance.</p>
            </section>
          </div>
        </DialogPanel>
      </Dialog>

      <PlayerShopSection />
    </div>
  );
};

export default Home;
